import {useRouter} from 'next/router';
import PropTypes from 'prop-types';
import {pathOr} from 'ramda';
import {useCallback, useEffect} from 'react';
import {useDispatch} from 'react-redux';

import {RESPONSE_STATUSES} from '@/src/app/constants/api';
import {NEW_COURSE_ID} from '@/src/app/constants/course';
import {
  resetCourseData,
  setEditCourse,
} from '@/src/app/store/createCourse/createCourseSlice';
import useCreateCourseActions from '@/src/app/store/createCourse/useCreateCourseActions';

import getCourseLanguages from '../../../../creatorStudio/courseLanguagesList/utils/getCourseLanguages';

const prepareCourse = (translations, selectedLanguages) => {
  return selectedLanguages.reduce((previous, currenLanguage) => {
    return {
      ...previous,
      [currenLanguage]: prepareTranslation(
        translations.find(
          (translation) => translation.language === currenLanguage
        )
      ),
    };
  }, {});
};

const prepareTranslation = (translation) => {
  const surgeryDate = pathOr(undefined, ['surgery_date'], translation);

  return {
    id: pathOr('', ['id'], translation),
    title: pathOr('', ['title'], translation),
    description: pathOr('', ['description'], translation),
    requirements: pathOr('', ['requirements'], translation),
    targetGroup: pathOr('', ['target_group'], translation),
    video: pathOr('', ['preview_video_url'], translation),
    videoIdentificator: pathOr(
      '',
      ['preview_video_identificator'],
      translation
    ),
    previewVideoFileName: pathOr(
      null,
      ['preview_video_file_name'],
      translation
    ),
    standardPreview: pathOr('', ['preview_image'], translation),
    originalImage: pathOr('', ['original_preview_image'], translation),
    previewImage: pathOr('', ['preview_image_filename'], translation),
    verticalPreview: pathOr('', ['preview_image_vertically'], translation),
    topics: prepareTopics(pathOr([], ['topics'], translation)),
    surgery_date: surgeryDate,
    co_authors: pathOr([], ['co_authors'], translation),
  };
};

export const prepareTopics = (topics) => {
  return topics.map((topic) => ({
    id: topic.id,
    isEditMode: false,
    title: topic.title,
    description: topic.description,
    technicalFeature: topic.technical_feature,
    links: topic.link_attachments,
    files: topic.pdf_attachments,
    videos: topic?.videos?.map((video) => ({
      ...video,
      video_url_isError: video.video_url || null,
    })),
  }));
};

const CourseCreationContainer = ({children}) => {
  const {
    query: {courseId},
  } = useRouter();
  const dispatch = useDispatch();
  const {currentLang} = useCreateCourseActions();

  const uploadExitCourse = useCallback(async () => {
    const {status, responseBody} = await getCourseLanguages(courseId);

    if (status === RESPONSE_STATUSES.SUCCESS) {
      const course = pathOr({}, ['data', 0], responseBody);

      const courseTranslations = prepareCourse(
        course.course_translations,
        course.languages
      );

      dispatch(
        setEditCourse({
          selectedLanguages: course.languages,
          difficultyLevel: course.difficulty_level,
          id: course.id,
          currentLang: course.languages.includes(currentLang)
            ? currentLang
            : course.languages[0],
          categories: course.categories.reduce(
            (previousValue, currentValue) => {
              return [
                currentValue.id,
                ...previousValue,
                ...currentValue.children.map((child) => child.id),
              ];
            },
            []
          ),
          ...courseTranslations,
        })
      );
    }
  }, [courseId]);

  useEffect(() => {
    if (courseId && courseId === NEW_COURSE_ID) {
      dispatch(resetCourseData());
    }

    if (courseId && courseId !== NEW_COURSE_ID) {
      uploadExitCourse();
    }
  }, [courseId]);

  return <>{children}</>;
};

export default CourseCreationContainer;

CourseCreationContainer.propTypes = {
  children: PropTypes.node.isRequired,
};
